/**
 * MKS-534 — on a project with nobody external on it, every ticket is internal
 * by construction, so the green "Internal" chip on every single board card is
 * noise. This context carries that one board-level answer down to the cards.
 *
 * Same shape as `blockerHighlightContext.tsx`, and for the same reason: the
 * answer belongs to the BOARD (one query for the whole page), the chip is
 * rendered by cards several levels down, and a card must keep rendering
 * standalone — the drag overlay, the external portal, the unit tests. The
 * fallback when no provider is mounted is therefore "hide nothing", which is
 * exactly today's board.
 *
 * Scoped by project id rather than a bare boolean because the merged
 * parent + subprojects board (MKS-419) shows cards of OTHER projects, whose
 * external roster this provider never asked about. Those cards keep their
 * chip: we hide only where we know the answer is "no external collaborators".
 *
 * The answer itself is MKS-532's `GET /api/projects/:id/comment-audience`,
 * deliberately the same one the comment composer uses to decide whether an
 * audience control is worth showing — the two surfaces must never disagree
 * about whether a project has anyone external on it.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useFlag } from '../../services/flagsContext';
import { useCommentAudience } from '../../hooks/useCommentAudience';

export interface InternalChipVisibility {
  /** Suppress the green "Internal" chip on a card of this project? */
  isInternalChipHidden(projectId: number): boolean;
}

const INERT: InternalChipVisibility = {
  isInternalChipHidden: () => false,
};

const InternalChipVisibilityContext = createContext<InternalChipVisibility | null>(null);

export interface InternalChipVisibilityProviderProps {
  /** The board's own project; null when the route param is not a number. */
  projectId: number | null;
  /** The external portal never renders the chip at all — do not query there. */
  audience: 'internal' | 'external';
  children: ReactNode;
}

export function InternalChipVisibilityProvider({
  projectId,
  audience,
  children,
}: InternalChipVisibilityProviderProps) {
  const flagOn = useFlag('mks_534_hide_internal_label');
  // Fail-safe twice over: with the flag off the query never fires (so a dark
  // feature costs no request), and `data` stays undefined, which reads as
  // "do not hide" below.
  const { data } = useCommentAudience(projectId, flagOn && audience === 'internal');

  const value = useMemo<InternalChipVisibility>(() => {
    // `=== false` and not `!...`: while the query is loading or failed there is
    // no answer, and an unknown roster must leave the board as it is.
    const hidden = flagOn && projectId !== null && data?.hasExternalCollaborators === false;
    return {
      isInternalChipHidden: (cardProjectId: number) => hidden && cardProjectId === projectId,
    };
  }, [flagOn, projectId, data]);

  return (
    <InternalChipVisibilityContext.Provider value={value}>
      {children}
    </InternalChipVisibilityContext.Provider>
  );
}

/** The board's chip rule, or an inert "hide nothing" when no provider is mounted. */
export function useInternalChipVisibility(): InternalChipVisibility {
  return useContext(InternalChipVisibilityContext) ?? INERT;
}
